import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  MessageCircle, 
  Clock, 
  Users, 
  Star,
  Gift,
  Check,
  Send,
  Phone,
  Mail,
  Shield,
  Headphones,
  Circle
} from 'lucide-react'

interface ChatMessage {
  id: number
  type: 'system' | 'agent' | 'user'
  message: string
  timestamp: Date
  agent?: {
    name: string
    avatar: string
    status: 'online' | 'typing' | 'away'
  }
}

interface Offer {
  id: number
  campaign_id: number
  title: string
  description: string
  click_url: string
  image: string
  cta_yes: string
  cta_no: string
  advertiser_name: string
  pixel: string
  beacons: {
    close: string
    no_thanks_click: string
  }
}

interface ApiResponse {
  data: {
    session_id: string
    offers: Offer[]
  }
}

const CustomerSupportPage: React.FC = () => {
  const navigate = useNavigate()
  const [waitTime, setWaitTime] = useState(180) // 3 minutes in seconds
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOffers, setSelectedOffers] = useState<Set<number>>(new Set())
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0)
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [userMessage, setUserMessage] = useState('')
  const [agentTyping, setAgentTyping] = useState(false)
  const [supportStage, setSupportStage] = useState<'waiting' | 'connected' | 'chatting'>('waiting')
  const [queuePosition, setQueuePosition] = useState(3)
  const [currentAgent] = useState({
    name: 'Sarah Mitchell',
    avatar: '👩‍💼',
    status: 'online' as const,
    department: 'Customer Success',
    rating: 4.9,
    languages: ['English', 'Spanish']
  })

  // Fetch offers from the API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await fetch('https://api.adspostx.com/native/v2/offers.json?api_key=b9a31935-a222-4bdd-bfc6-a63e6bb01e39', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            country: 'us'
          })
        })

        const data: ApiResponse = await response.json()
        
        if (data.data && data.data.offers) {
          setOffers(data.data.offers)
        }
      } catch (error) {
        console.error('Failed to fetch offers:', error)
        // Fallback offers in case API fails
        setOffers([
          {
            id: 1,
            campaign_id: 1,
            title: "Get $15 Cash Back on Today's Purchase",
            description: "Get free shipping rebates on online purchases just for trying Shipments Free.",
            click_url: "#",
            image: "💰",
            cta_yes: "Claim Your $15",
            cta_no: "No, Thanks",
            advertiser_name: "Shipments Free",
            pixel: "",
            beacons: { close: "", no_thanks_click: "" }
          }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchOffers()
  }, [])

  // Countdown timer and queue simulation
  useEffect(() => {
    const timer = setInterval(() => {
      setWaitTime(prev => {
        if (prev > 0) {
          // Simulate queue movement
          if (prev % 30 === 0 && queuePosition > 1) {
            setQueuePosition(curr => curr - 1)
          }
          
          // Connect when timer reaches 30 seconds
          if (prev === 30) {
            setSupportStage('connected')
            // Initial system messages
            setChatMessages([
              {
                id: 1,
                type: 'system',
                message: 'You have been connected to our support team. Please wait while we find an available agent.',
                timestamp: new Date()
              }
            ])
            
            // Agent connection after 5 seconds
            setTimeout(() => {
              setSupportStage('chatting')
              setChatMessages(prev => [...prev, {
                id: 2,
                type: 'agent',
                message: `Hi! I'm ${currentAgent.name} from ${currentAgent.department}. Thanks for waiting! How can I help you today?`,
                timestamp: new Date(),
                agent: currentAgent
              }])
            }, 5000)
          }
          
          return prev - 1
        }
        return 0
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [queuePosition, currentAgent])

  // Auto-rotate offers every 10 seconds
  useEffect(() => {
    if (offers.length > 1) {
      const interval = setInterval(() => {
        setCurrentOfferIndex(prev => (prev + 1) % offers.length)
      }, 10000)

      return () => clearInterval(interval)
    }
  }, [offers.length])

  const handleSendMessage = () => {
    if (userMessage.trim() && supportStage === 'chatting') {
      const newMessage: ChatMessage = {
        id: chatMessages.length + 1,
        type: 'user',
        message: userMessage,
        timestamp: new Date()
      }
      
      setChatMessages(prev => [...prev, newMessage])
      setUserMessage('')
      
      // Simulate agent typing and response
      setAgentTyping(true)
      setTimeout(() => {
        setAgentTyping(false)
        
        const responses = [
          "I understand your concern. Let me look into that for you right away.",
          "That's a great question! I'll need to check a few details to give you the best answer.",
          "Thank you for providing that information. I'm reviewing your account now.",
          "I can definitely help you with that. Let me pull up the relevant information.",
          "I appreciate your patience. I'm working on getting you the best solution.",
          "That makes sense. Let me check our current options for you."
        ]
        
        const randomResponse = responses[Math.floor(Math.random() * responses.length)]
        
        setChatMessages(prev => [...prev, {
          id: prev.length + 1,
          type: 'agent',
          message: randomResponse,
          timestamp: new Date(),
          agent: currentAgent
        }])
      }, 2000 + Math.random() * 2000) // Random delay between 2-4 seconds
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleOfferAction = (offerId: number, action: 'yes' | 'no') => {
    const offer = offers.find(o => o.id === offerId)
    if (!offer) return

    if (action === 'yes') {
      setSelectedOffers(prev => new Set([...prev, offerId]))
      // Track impression pixel
      if (offer.pixel) {
        const img = new Image()
        img.src = offer.pixel.replace('%7BCBUSTER%7D', Date.now().toString())
      }
    } else {
      // Track "no thanks" beacon
      if (offer.beacons.no_thanks_click) {
        fetch(offer.beacons.no_thanks_click, { method: 'GET' }).catch(console.error)
      }
    }
  }

  const currentOffer = offers[currentOfferIndex]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/moments-gallery')}
            className="flex items-center space-x-2 px-4 py-2 bg-white shadow-sm hover:shadow-md text-gray-600 hover:text-gray-800 rounded-lg transition-all duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Gallery</span>
          </button>
          
          {/* Company Header */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">TechFlow Support</h1>
                <p className="text-sm text-gray-500">We're here to help</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-green-600">
              <Circle className="w-2 h-2 fill-current" />
              <span className="text-sm font-medium">Online</span>
            </div>
          </div>
        </motion.div>

        {/* Support Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">Avg. Response</p>
                <p className="font-semibold text-gray-900">2-3 min</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Users className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-500">Agents Online</p>
                <p className="font-semibold text-gray-900">12</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-sm text-gray-500">Satisfaction</p>
                <p className="font-semibold text-gray-900">4.8/5</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-500">Security</p>
                <p className="font-semibold text-gray-900">256-bit SSL</p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Support Chat Interface */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg">
                        {supportStage === 'waiting' ? '🔄 Connecting You...' : 
                         supportStage === 'connected' ? '🔍 Finding Perfect Agent...' : 
                         `💬 Chat with ${currentAgent.name}`}
                      </h2>
                      <div className="flex items-center space-x-2 text-sm opacity-90">
                        <div className={`w-2.5 h-2.5 rounded-full ${
                          supportStage === 'waiting' ? 'bg-yellow-400 animate-pulse' :
                          supportStage === 'connected' ? 'bg-blue-400 animate-pulse' : 'bg-green-400'
                        }`}></div>
                        <span>
                          {supportStage === 'waiting' ? 
                            (queuePosition === 1 ? 'Next in line - Premium queue' : `Priority position #${queuePosition}`) :
                           supportStage === 'connected' ? 'Matching you with specialist...' :
                           `${currentAgent.department} Expert • ${currentAgent.rating}★ rating`}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {supportStage === 'chatting' && (
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 opacity-75 hover:opacity-100 cursor-pointer" />
                      <Mail className="w-4 h-4 opacity-75 hover:opacity-100 cursor-pointer" />
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Body */}
              <div className="h-[28rem] flex flex-col">
                {supportStage === 'waiting' ? (
                  /* Wait Queue Interface */
                  <>
                    <div className="flex-1 p-6 space-y-6">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-lg font-bold text-blue-900 mb-1">Thanks for choosing TechFlow Support!</p>
                            <p className="text-sm text-blue-700 leading-relaxed">
                              {queuePosition === 1 ? (
                                <>You're <span className="font-bold text-blue-900">next in line</span>! We'll connect you with our top-rated agent in approximately <span className="font-bold text-blue-900">{formatTime(waitTime)}</span>.</>
                              ) : (
                                <>You're position <span className="font-bold text-blue-900">#{queuePosition}</span> in our priority queue. Expected connection time: <span className="font-bold text-blue-900">{formatTime(waitTime)}</span>.</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-8"
                      >
                        <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl px-8 py-5 shadow-sm">
                          <Clock className="w-10 h-10 text-amber-600" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-amber-800 mb-1">Connection in progress</p>
                            <p className="text-3xl font-bold text-amber-900 tracking-tight">{formatTime(waitTime)}</p>
                            <p className="text-xs text-amber-700 mt-1">
                              {queuePosition === 1 ? 'Preparing your session...' : `${queuePosition - 1} customer${queuePosition > 2 ? 's' : ''} ahead`}
                            </p>
                          </div>
                        </div>

                        <div className="mt-8">
                          <p className="text-gray-700 mb-4 text-center font-medium px-4 leading-relaxed">
                            💎 While you wait, discover these <span className="text-blue-600 font-semibold">premium offers</span> curated just for you!
                          </p>
                          <div className="flex justify-center space-x-1">
                            {[1, 2, 3].map((dot) => (
                              <motion.div
                                key={dot}
                                className="w-3 h-3 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full"
                                animate={{
                                  scale: [1, 1.3, 1],
                                  opacity: [0.6, 1, 0.6]
                                }}
                                transition={{
                                  duration: 1.8,
                                  repeat: Infinity,
                                  delay: dot * 0.3
                                }}
                              />
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 text-center mt-3 italic px-2">
                            Powered by AI-driven personalization
                          </p>
                        </div>
                      </motion.div>
                    </div>
                    
                    {/* Chat Input - Always Visible */}
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={userMessage}
                          onChange={(e) => setUserMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Describe your issue while you wait..."
                          className="flex-1 border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                          disabled={supportStage === 'waiting'}
                        />
                        <button 
                          onClick={handleSendMessage}
                          disabled={!userMessage.trim() || supportStage === 'waiting'}
                          className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        {supportStage === 'waiting' ? 'Chat will be enabled once connected to an agent' : 'Type your message and press Enter'}
                      </p>
                    </div>
                  </>
                ) : (
                  /* Chat Interface */
                  <>
                    <div className="flex-1 p-4 overflow-y-auto space-y-4">
                      {chatMessages.map((msg) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-xs lg:max-w-md ${
                            msg.type === 'user' 
                              ? 'bg-blue-500 text-white rounded-lg rounded-br-none' 
                              : msg.type === 'system'
                              ? 'bg-gray-100 text-gray-700 rounded-lg'
                              : 'bg-white border border-gray-200 rounded-lg rounded-bl-none'
                          } p-3`}>
                            {msg.type === 'agent' && msg.agent && (
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-lg">{msg.agent.avatar}</span>
                                <span className="text-xs font-medium text-gray-600">{msg.agent.name}</span>
                              </div>
                            )}
                            <p className="text-sm">{msg.message}</p>
                            <p className="text-xs opacity-75 mt-1">
                              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                      
                      {agentTyping && (
                        <div className="flex justify-start">
                          <div className="bg-white border border-gray-200 rounded-lg p-3 max-w-xs">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-lg">{currentAgent.avatar}</span>
                              <span className="text-xs font-medium text-gray-600">{currentAgent.name}</span>
                            </div>
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="border-t border-gray-200 p-4">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={userMessage}
                          onChange={(e) => setUserMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Type your message..."
                          className="flex-1 border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <button 
                          onClick={handleSendMessage}
                          disabled={!userMessage.trim()}
                          className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>

          {/* Right Column - Offers Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Exclusive Offers</h3>
                  <p className="text-sm text-gray-600">
                    {supportStage === 'waiting' ? 'While you wait' : 'Just for you'}
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Loading personalized offers...</p>
                  <p className="text-xs text-gray-500 mt-2">Powered by MomentScience</p>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  {currentOffer && (
                    <motion.div
                      key={currentOffer.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="border border-gray-200 rounded-lg p-4 mb-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="text-3xl bg-gray-50 rounded-lg p-2">
                          {currentOffer.image.startsWith('http') ? '🎁' : currentOffer.image}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm mb-1 leading-tight">
                            {currentOffer.title}
                          </h4>
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                            {currentOffer.description}
                          </p>
                          <div className="flex items-center space-x-2">
                            <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                              {currentOffer.advertiser_name}
                            </div>
                            <div className="text-xs text-green-600 font-medium">
                              Limited Time
                            </div>
                          </div>
                        </div>
                      </div>

                      {selectedOffers.has(currentOffer.id) ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                          <Check className="w-5 h-5 text-green-600 mx-auto mb-1" />
                          <p className="text-sm text-green-700 font-medium">Offer Saved!</p>
                          <p className="text-xs text-green-600 mt-1">We'll email you the details</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <button
                            onClick={() => handleOfferAction(currentOffer.id, 'yes')}
                            className="w-full py-2.5 px-4 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-pink-600 hover:to-orange-600 transition-all duration-200 shadow-sm hover:shadow-md"
                          >
                            {currentOffer.cta_yes}
                          </button>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}

              {/* Offers Counter */}
              {offers.length > 1 && (
                <div className="text-center mt-4">
                  <div className="flex justify-center space-x-1 mb-2">
                    {offers.map((_, index) => (
                      <div
                        key={index}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          index === currentOfferIndex ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    {currentOfferIndex + 1} of {offers.length} exclusive offers
                  </p>
                </div>
              )}

              {/* Selected Offers Summary */}
              {selectedOffers.size > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-gray-200"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-medium text-gray-900">
                      {selectedOffers.size} offer{selectedOffers.size !== 1 ? 's' : ''} saved
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Your selected offers will be emailed to you after this session.
                  </p>
                </motion.div>
              )}

              {/* Support Context */}
              {supportStage !== 'waiting' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-6 pt-4 border-t border-gray-200"
                >
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-2">
                      Need help with these offers?
                    </p>
                    <button 
                      onClick={() => {
                        if (supportStage === 'chatting') {
                          setUserMessage("I'd like to know more about these offers.")
                          setTimeout(handleSendMessage, 100)
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Ask our agent
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Additional Support Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-4"
            >
              <h4 className="font-semibold text-gray-900 mb-3 text-sm">Other Ways to Reach Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-sm">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">Call Support</p>
                    <p className="text-gray-500 text-xs">1-800-TECH-FLOW</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <Mail className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">Email Us</p>
                    <p className="text-gray-500 text-xs">support@techflow.com</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 bg-white rounded-xl shadow-sm border border-gray-100 p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <Headphones className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">TechFlow Support</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Providing world-class customer support with AI-powered personalization. 
                Our expert agents are available 24/7 to help you with any questions or concerns.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>SOC 2 Certified</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span>4.8/5 Rating</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><a href="#" className="hover:text-blue-600 transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Knowledge Base</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Community Forum</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Service Status</a></li>
                <li><a href="#" className="hover:text-blue-600 transition-colors">Contact Us</a></li>
              </ul>
            </div>

            {/* Support Hours */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Support Hours</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Chat Support</span>
                  <span className="text-green-600 font-medium">24/7</span>
                </div>
                <div className="flex justify-between">
                  <span>Phone Support</span>
                  <span>6AM - 10PM PST</span>
                </div>
                <div className="flex justify-between">
                  <span>Email Support</span>
                  <span>&lt; 2hr response</span>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Circle className="w-2 h-2 fill-current text-green-500" />
                    <span className="text-xs font-medium text-blue-900">All systems operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-sm text-gray-500">
                © 2025 TechFlow Support. All rights reserved. | 
                <a href="#" className="hover:text-blue-600 ml-1">Privacy Policy</a> | 
                <a href="#" className="hover:text-blue-600 ml-1">Terms of Service</a>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-xs text-gray-500">
                  Powered by MomentScience AI
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-orange-500 rounded flex items-center justify-center">
                    <Gift className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-xs text-gray-600">Smart Offers</span>
                </div>
              </div>
            </div>
          </div>
        </motion.footer>
      </div>
    </div>
  )
}

export default CustomerSupportPage
